import { Module } from 'vuex';
import type { RootState } from '@/interfaces/store';
import type { ActionContext } from '@/interfaces/common';
import { serieInitialData } from '@/constants/graphics';
import $rq from '@/api/api';

// Interfaces
interface Serie {
    id: string;
    component: string | null;
    analysisType: string | null;
}

interface Graphic {
    id: string;
    name?: string;
    series: Serie[];
}

interface GraphicsState {
    graphics: Graphic[];
}

interface SetSeriesPayload {
    id: string;
    series: Serie[];
}

interface AddGraphicPayload {
    siteId: string;
    graphic: Partial<Graphic>;
}

interface EditGraphicPayload {
    siteId: string;
    graphicId: string;
    graphic: Partial<Graphic>;
}

interface DeleteGraphicPayload {
    siteId: string;
    graphicId: string;
}

interface ResetSeriesPayload {
    siteId: string;
    graphicId: string;
}

// State
const state: GraphicsState = {
    graphics: [],
};

// Mutations
const mutations = {
    SET_GRAPHICS(state: GraphicsState, graphics: Graphic[]) {
        state.graphics = graphics;
    },
    SET_SERIES(state: GraphicsState, payload: SetSeriesPayload) {
        const { id, series } = payload;
        const indexId = state.graphics.findIndex(({ id: graphicId }) => id === graphicId);
        if (indexId !== -1) {
            state.graphics[indexId].series = series;
        }
    },
};

// Actions
const actions = {
    async getGraphics({ commit }: ActionContext, siteId: string) {
        const {
            data: { graphics },
        } = await $rq.getGraphics(siteId);
        commit('SET_GRAPHICS', graphics);
    },

    setSeries({ commit }: ActionContext, payload: SetSeriesPayload) {
        const { id, series } = payload;
        commit('SET_SERIES', { id, series });
    },

    async addGraphic({ dispatch }: ActionContext, payload: AddGraphicPayload) {
        const { siteId, graphic } = payload;
        await $rq.addGraphic(siteId, graphic);
        dispatch('getGraphics', siteId);
    },

    async editGraphic({ dispatch }: ActionContext, payload: EditGraphicPayload) {
        const { siteId, graphicId, graphic } = payload;
        await $rq.editGraphic(siteId, graphicId, graphic);
        dispatch('getGraphics', siteId);
    },

    async deleteGraphic({ dispatch }: ActionContext, payload: DeleteGraphicPayload) {
        const { siteId, graphicId } = payload;
        await $rq.deleteGraphic(siteId, graphicId);
        dispatch('getGraphics', siteId);
    },

    async resetAllGraphicSeries(_: ActionContext, payload: ResetSeriesPayload) {
        const { siteId, graphicId } = payload;
        const getSeries = await $rq.getSeries(siteId, graphicId);
        const {
            data: { series },
        } = getSeries;
        await Promise.all(
            series.map(({ id }) => $rq.updateSerie(siteId, graphicId, id, serieInitialData)),
        );
    },
};

// Getters
const getters = {
    graphics: (state: GraphicsState): Graphic[] => state.graphics,
    getGraphic:
        (state: GraphicsState) =>
        (graphicId: string): Graphic | undefined => {
            return state.graphics.find(({ id }) => id === graphicId);
        },
    getSeries:
        (state: GraphicsState) =>
        (graphicId: string): Serie[] | undefined => {
            const graphic = state.graphics.find(({ id }) => id === graphicId);
            return graphic?.series;
        },
};

const graphicsModule: Module<GraphicsState, RootState> = {
    state,
    getters,
    actions,
    mutations,
};

export default graphicsModule;
